import React, { useContext, useEffect, useRef, useState } from "react";
import secureLocalStorage from "react-secure-storage";
import { MyContext } from "../../Context/MyProvider";
import ImageLoad from "../ImageLoad";
import axios from "axios";
import { useTranslation } from "react-i18next";
import { showToast } from "../../showTost";
import {
  LuUser,
  LuMail,
  LuPhone,
  LuLock,
  LuSave,
  LuPencil,
  LuChevronRight,
} from "react-icons/lu";
import { IoChevronBackOutline } from "react-icons/io5";
import { useNavigate } from "react-router-dom";

const Profile = () => {
  const { t } = useTranslation();
  const { ApiBaseUrl, ImgBaseUrl, setIsLogin, isLogin } = useContext(MyContext);
  const navigate = useNavigate();

  const [profilePic, setProfilePic] = useState(null);
  const [selectedFile, setSelectedFile] = useState(null);
  const [imgError, setImgError] = useState(false);
  const [data, setData] = useState({ name: "", email: "" });
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [number, setNumber] = useState("");
  const [password, setPassword] = useState("");
  const [countryCode, setCountryCode] = useState("");

  const FileRef = useRef(null);

  useEffect(() => {
    const LoginData = secureLocalStorage.getItem("Loginuser");
    if (LoginData) {
      const { country_code, name, email, phone, image } = JSON.parse(LoginData);
      setCountryCode(country_code || "+91");
      setName(name || "");
      setEmail(email || "");
      setNumber(phone || "");
      if (
        image &&
        typeof image === "string" &&
        image !== "null" &&
        image !== "undefined" &&
        !image.startsWith("blob:")
      ) {
        const fullUrl = image.startsWith("http") ? image : ImgBaseUrl + image;
        setProfilePic(fullUrl);
        setImgError(false);
      } else {
        setProfilePic(null);
      }
      setData({ name: name || "", email: email || "" });
    }
  }, [isLogin, ImgBaseUrl]);

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const allowedTypes = ["image/jpeg", "image/png", "image/webp", "image/jpg"];
    if (!allowedTypes.includes(file.type)) {
      showToast({
        title: t("Only JPG, PNG, WEBP files are allowed!"),
        id: "error",
      });
      e.target.value = "";
      return;
    }
    setSelectedFile(file);
    const imageUrl = URL.createObjectURL(file);
    setProfilePic(imageUrl);
    setImgError(false);
  };

  const HandleEditProfile = (e) => {
    e.preventDefault();
    const Userid = secureLocalStorage.getItem("Userid");

    if (!name || !email || !number) {
      showToast({ title: t("Enter details"), id: "error" });
      return;
    }

    const formData = new FormData();
    formData.append("id", Userid);
    if (selectedFile) {
      formData.append("image", selectedFile);
    }
    formData.append("name", name);
    formData.append("email", email);
    formData.append("password", password ? password : "");
    formData.append("ccode", countryCode || "+91");
    formData.append("phone", number);

    axios
      .post(`${ApiBaseUrl}/customer/edit_profile`, formData)
      .then((res) => {
        if (res.data.ResponseCode === 200) {
          showToast({ title: res.data.message, id: "success" });
          const updated = res.data.customer_detail;
          secureLocalStorage.setItem(
            "Loginuser",
            JSON.stringify(updated),
          );
          if (updated?.image) {
            const fullUrl = updated.image.startsWith("http")
              ? updated.image
              : ImgBaseUrl + updated.image;
            setProfilePic(fullUrl);
            setImgError(false);
          }
          setSelectedFile(null);
          setIsLogin(Date.now().toString());
        } else {
          showToast({
            title: res.data.message || t("Profile update failed"),
            id: "error",
          });
        }
      })
      .catch((error) => {
        console.error(error);
        showToast({ title: t("Profile update failed"), id: "error" });
      });
  };

  // Get initials for avatar fallback
  const initials = data?.name
    ? data.name
        .split(" ")
        .map((w) => w[0])
        .slice(0, 2)
        .join("")
        .toUpperCase()
    : "?";

  const inputClass =
    "w-full h-11 rounded-2xl border border-slate-200 bg-slate-50 px-4 py-2 text-sm font-medium text-slate-800 placeholder:text-slate-400 shadow-sm transition-all focus:outline-none focus:ring-2 focus:ring-indigo-600 focus:border-transparent hover:border-slate-300";

  const fields = [
    {
      label: t("Full Name"),
      icon: <LuUser className="w-4 h-4 shrink-0 text-indigo-500" />,
      input: (
        <input
          className={inputClass}
          required
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder={t("Full Name")}
        />
      ),
    },
    {
      label: t("Email Address"),
      icon: <LuMail className="w-4 h-4 shrink-0 text-indigo-500" />,
      input: (
        <input
          className={inputClass}
          type="email"
          required
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder={t("Email Address")}
        />
      ),
    },
    {
      label: t("Phone Number"),
      icon: <LuPhone className="w-4 h-4 shrink-0 text-indigo-500" />,
      input: (
        <input
          className={inputClass}
          required
          value={number}
          onChange={(e) => setNumber(e.target.value)}
          placeholder={t("Phone Number")}
        />
      ),
    },
    {
      label: t("Password"),
      icon: <LuLock className="w-4 h-4 shrink-0 text-indigo-500" />,
      input: (
        <input
          className={inputClass}
          type="password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          placeholder={t("Enter Password (Optional)")}
        />
      ),
    },
  ];

  return (
    <div className="profile-page-container w-full min-h-screen pb-[100px] content-wrapper px-4 md:px-6 max-w-7xl mx-auto">
      {/* Header Navigation */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 py-4 mb-6 border-b border-slate-100">
        <button
          onClick={() => window.history.back()}
          className="inline-flex items-center gap-2 border border-slate-200 rounded-full px-4 py-2 hover:bg-slate-900 hover:text-white hover:border-slate-900 transition-all duration-200 shadow-sm"
        >
          <IoChevronBackOutline className="w-4 h-4" />
          <span className="font-medium text-sm">{t("Go Back")}</span>
        </button>
        <div className="flex items-center gap-2 text-sm text-slate-500">
          <span
            className="hover:text-indigo-600 font-medium transition-colors cursor-pointer"
            onClick={() => navigate("/dashbord")}
          >
            {t("Dashboard")}
          </span>
          <LuChevronRight className="w-3.5 h-3.5" />
          <span className="font-semibold text-slate-800">
            {t("My Profile")}
          </span>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-2xl mx-auto">
        {/* Avatar Banner */}
        <div className="bg-gradient-to-br from-indigo-600 via-indigo-500 to-violet-600 rounded-3xl p-8 mb-6 flex flex-col sm:flex-row items-center sm:items-end gap-6 shadow-lg shadow-indigo-200 relative overflow-hidden">
          {/* decorative blobs */}
          <div className="absolute -top-10 -right-10 w-48 h-48 bg-white/10 rounded-full blur-2xl pointer-events-none" />
          <div className="absolute -bottom-10 -left-10 w-36 h-36 bg-white/10 rounded-full blur-2xl pointer-events-none" />

          {/* Avatar */}
          <div className="relative shrink-0">
            <div className="w-24 h-24 sm:w-28 sm:h-28 rounded-3xl overflow-hidden border-4 border-white/30 shadow-xl bg-white/20 flex items-center justify-center">
              {profilePic && !imgError ? (
                <img
                  src={profilePic}
                  alt="profile"
                  onError={() => setImgError(true)}
                  className="w-full h-full object-cover"
                />
              ) : (
                <div className="w-full h-full flex items-center justify-center bg-indigo-500/40 text-white font-black text-3xl">
                  {initials}
                </div>
              )}
            </div>
            {/* Edit photo button */}
            <button
              onClick={() => FileRef.current.click()}
              className="absolute -bottom-2 -right-2 w-9 h-9 bg-white rounded-2xl border border-slate-100 shadow-md flex items-center justify-center hover:bg-indigo-50 hover:border-indigo-200 transition-all duration-150 active:scale-90"
            >
              <LuPencil className="w-4 h-4 text-indigo-600" />
            </button>
            <input
              type="file"
              accept="image/*"
              ref={FileRef}
              className="hidden"
              onChange={handleFileChange}
            />
          </div>

          {/* Name & Email */}
          <div className="text-white text-center sm:text-left z-10">
            <h2 className="font-black text-2xl sm:text-3xl leading-tight">
              {data?.name || "—"}
            </h2>
            <p className="text-white/70 text-sm font-medium mt-0.5">
              {data?.email || "—"}
            </p>
            <span className="mt-3 inline-block bg-white/20 border border-white/30 text-white text-[11px] font-bold uppercase tracking-wider px-3 py-1 rounded-full backdrop-blur-sm">
              {t("Verified Member")}
            </span>
          </div>
        </div>

        {/* Form Card */}
        <div className="bg-white rounded-3xl border border-slate-150 shadow-sm p-6 sm:p-8">
          <div className="mb-6">
            <h3 className="font-bold text-slate-800 text-xl">
              {t("Edit Profile")}
            </h3>
            <p className="text-slate-400 text-sm font-medium mt-0.5">
              {t("Update your personal information")}
            </p>
          </div>

          <form onSubmit={HandleEditProfile} className="flex flex-col gap-5">
            {fields.map((field, i) => (
              <div key={i} className="flex flex-col gap-1.5">
                <label className="flex items-center gap-2 text-sm font-bold text-slate-600">
                  {field.icon}
                  {field.label}
                </label>
                {field.input}
              </div>
            ))}

            <button
              type="submit"
              className="mt-2 w-full h-12 bg-indigo-600 hover:bg-indigo-700 active:scale-95 text-white font-bold text-sm rounded-2xl flex items-center justify-center gap-2.5 shadow-md shadow-indigo-600/20 transition-all duration-150"
            >
              <LuSave className="w-4 h-4" />
              {t("Save Changes")}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
};

export default Profile;
